// Browser-side Apple Pay / Google Pay for STOVE online ordering.
// Single-shot model: the tip is already in `amount`, so each wallet does ONE
// SecureAuth sale via /sys/v4/online/payments/wallet-sale and returns a result
// shaped like the card endpoint: { transactionid, lastfour, authid, cardtype }.
// Resolves null when the user cancels the sheet; rejects on a real failure.
import { api } from './client';

const APPLE_SUPPORTED_NETWORKS = ['visa', 'masterCard', 'amex', 'discover'];

// ── Apple Pay ───────────────────────────────────────────────────────────────
export function isApplePayAvailable() {
  try {
    return !!(window.ApplePaySession && window.ApplePaySession.canMakePayments());
  } catch (e) {
    return false;
  }
}

// NOTE: must be called synchronously from a user gesture (button click) so that
// session.begin() fires inside the gesture — do not await anything before this.
export function startApplePay({ venueid, amount, merchantDisplayName }) {
  return new Promise((resolve, reject) => {
    if (!isApplePayAvailable()) {
      return reject(new Error('Apple Pay is not available on this device'));
    }

    const paymentRequest = {
      countryCode: 'US',
      currencyCode: 'USD',
      merchantCapabilities: ['supports3DS'],
      supportedNetworks: APPLE_SUPPORTED_NETWORKS,
      // Ask Apple Pay to return the cardholder's name so callers can use it
      // (e.g. prefill/record). The checkout form remains the required source.
      requiredBillingContactFields: ['name'],
      total: { label: merchantDisplayName || 'Online Order', amount: Number(amount).toFixed(2) },
    };

    let session;
    try {
      session = new window.ApplePaySession(3, paymentRequest);
    } catch (e) {
      return reject(new Error('Could not start Apple Pay'));
    }

    session.onvalidatemerchant = async (event) => {
      try {
        const merchantSession = await api.validateWalletSession({
          venueid,
          validationURL: event.validationURL,
          hostname: window.location.hostname,
        });
        if (!merchantSession || merchantSession.result === 0) {
          throw new Error((merchantSession && (merchantSession.message || merchantSession.error)) || 'Merchant validation failed');
        }
        session.completeMerchantValidation(merchantSession);
      } catch (err) {
        try { session.abort(); } catch (e) {}
        reject(err);
      }
    };

    session.onpaymentauthorized = async (event) => {
      try {
        const token = event.payment.token.paymentData; // ApplePayPaymentToken.paymentData
        // Card network (e.g. "Visa", "MasterCard") from the selected payment
        // method — the reliable source for the real card type.
        const pm = (event.payment.token && event.payment.token.paymentMethod) || {};
        const cardNetwork = pm.network || '';
        // Cardholder name (only present because requiredBillingContactFields
        // requested it). Available to callers; may be empty on some cards.
        const billing = event.payment.billingContact || {};
        const firstName = billing.givenName || '';
        const lastName = billing.familyName || '';
        const sale = await api.processWalletPayment({
          venueid,
          wallet_type: 'APPLE_PAY',
          paymentToken: JSON.stringify(token),
          amount: Number(amount).toFixed(2),
        });
        if (sale.result === 1) {
          session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
          resolve({
            transactionid: sale.transactionid || '',
            lastfour: sale.lastfour || '',
            authid: sale.authid || sale.authcode || '',
            cardtype: sale.cardtype || 'AP',
            cardNetwork: cardNetwork || sale.cardtype || '',
            firstName,
            lastName,
          });
        } else {
          session.completePayment(window.ApplePaySession.STATUS_FAILURE);
          reject(new Error(sale.message || sale.error || 'Apple Pay declined'));
        }
      } catch (err) {
        try { session.completePayment(window.ApplePaySession.STATUS_FAILURE); } catch (e) {}
        reject(err);
      }
    };

    session.oncancel = () => resolve(null); // user dismissed the sheet

    session.begin();
  });
}

// ── Google Pay ────────────────────────────────────────────────────────────────
let _gpScriptPromise = null;
function loadGooglePayScript() {
  if (_gpScriptPromise) return _gpScriptPromise;
  _gpScriptPromise = new Promise((resolve, reject) => {
    if (window.google && window.google.payments && window.google.payments.api) return resolve();
    const s = document.createElement('script');
    s.src = 'https://pay.google.com/gp/p/js/pay.js';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Google Pay'));
    document.head.appendChild(s);
  });
  return _gpScriptPromise;
}

const gpBaseRequest = () => ({ apiVersion: 2, apiVersionMinor: 0 });

function gpAllowedMethod(cfg) {
  return {
    type: 'CARD',
    parameters: {
      allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
      allowedCardNetworks: ['AMEX', 'DISCOVER', 'MASTERCARD', 'VISA'],
    },
    tokenizationSpecification: {
      type: 'PAYMENT_GATEWAY',
      parameters: {
        // gateway + gatewayMerchantId come from /wallet-config (per venue).
        // gateway must match what TSYS expects (default 'tsys'); override via
        // venueSettings.onlineSettings.googlePayGateway if TSYS uses a different id.
        gateway: cfg.googlePayGateway || 'tsys',
        gatewayMerchantId: cfg.googlePayGatewayMerchantId || '',
      },
    },
  };
}

let _gpClient = null;
function gpClient() {
  if (!_gpClient) {
    _gpClient = new window.google.payments.api.PaymentsClient({ environment: 'PRODUCTION' });
  }
  return _gpClient;
}

export async function isGooglePayAvailable(cfg) {
  if (!cfg || !cfg.googlePayEnabled || !cfg.googlePayGatewayMerchantId) return false;
  try {
    await loadGooglePayScript();
    const req = { ...gpBaseRequest(), allowedPaymentMethods: [gpAllowedMethod(cfg)] };
    const resp = await gpClient().isReadyToPay(req);
    return !!resp.result;
  } catch (e) {
    return false;
  }
}

export async function startGooglePay({ venueid, amount, cfg }) {
  await loadGooglePayScript();

  const paymentDataRequest = {
    ...gpBaseRequest(),
    allowedPaymentMethods: [gpAllowedMethod(cfg)],
    transactionInfo: {
      totalPriceStatus: 'FINAL',
      totalPrice: Number(amount).toFixed(2),
      currencyCode: 'USD',
      countryCode: 'US',
    },
    merchantInfo: {
      merchantName: cfg.merchantDisplayName || 'Online Order',
      ...(cfg.googlePayMerchantId ? { merchantId: cfg.googlePayMerchantId } : {}),
    },
  };

  let paymentData;
  try {
    paymentData = await gpClient().loadPaymentData(paymentDataRequest);
  } catch (e) {
    if (e && e.statusCode === 'CANCELED') return null; // user closed sheet
    throw new Error('Google Pay was cancelled or failed');
  }

  const token = paymentData.paymentMethodData.tokenizationData.token; // JSON string
  // Card network (e.g. "VISA", "MASTERCARD") from Google Pay's method info.
  const gpInfo = (paymentData.paymentMethodData && paymentData.paymentMethodData.info) || {};
  const cardNetwork = gpInfo.cardNetwork || '';
  const sale = await api.processWalletPayment({
    venueid,
    wallet_type: 'GOOGLE_PAY',
    paymentToken: token,
    amount: Number(amount).toFixed(2),
  });
  if (sale.result !== 1) throw new Error(sale.message || sale.error || 'Google Pay declined');
  return {
    transactionid: sale.transactionid || '',
    lastfour: sale.lastfour || '',
    authid: sale.authid || sale.authcode || '',
    cardtype: sale.cardtype || 'GP',
    cardNetwork: cardNetwork || sale.cardtype || '',
  };
}